// web/src/pages/Settings.tsx
import { toast } from "@/components/ui/use-toast";
import React, { useCallback, useEffect, useMemo, useState, useRef, useId } from "react";
import {
  onAuthStateChanged,
  type User,
  updateEmail,
  sendEmailVerification,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { auth, db, storage } from "@/lib/firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  startAt,
  endAt,
  limit,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
  onSnapshot,
  Timestamp,
  FieldValue,
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { claimUsername } from "@/data/username-claim";
import { searchLgsOSM } from "@/lib/osm-lgs";
import { getAutoLocationOnLogin, setAutoLocationOnLogin } from "@/data/locationPref";
import { geohashQueryBounds } from "geofire-common";
import { tryWpnBackfill } from "@/lib/wpnClient";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import LgsUpgradeModal from "@/components/lgs/LgsUpgradeModal"; // fixed import

// ADD (near top, after imports – before first usage of pushToast)
function pushToast(message: string, ttlMs = 1800) {
  toast({ description: message });
  if (ttlMs > 0) setTimeout(() => toast({ description: null }), ttlMs);
}

/* ----------------------------------------
   Username + format normalization
----------------------------------------- */
function normalizeUsername(username: string): string {
  return username
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9_-]/g, "");
}
function normalizeFormat(s: string): string | null {
  const raw = String(s ?? "").trim().toLowerCase();
  if (!raw) return null;
  const x = raw.replace(/\s+/g, " ");
  if (x === "edh" || x === "commander (edh)") return "commander";
  if (x === "c edh" || x === "c-edh" || x === "cedh") return "cedh";
  if (x === "sealed deck") return "sealed";
  if (x === "drafting") return "draft";
  if (x === "pauper edh" || x === "pdh" || x === "pedh") return "pauper commander";
  return x;
}

/* ----------------------------------------
   Slim accessible Switch (still used elsewhere)
----------------------------------------- */
function Switch({
  checked,
  onChange,
  disabled,
  label,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: string;
  id?: string;
}) {
  const autoId = useId();
  const inputId = id ?? `sw-${autoId}`;
  return (
    <label
      htmlFor={inputId}
      className={`inline-flex items-center gap-3 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <input
        id={inputId}
        type="checkbox"
        role="switch"
        aria-label={label ?? "toggle"}
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
          checked ? "bg-amber-400" : "bg-gray-600"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-black transition ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </span>
    </label>
  );
}

/* ----------------------------------------
   Commander Brackets
----------------------------------------- */
type BracketId = 1 | 2 | 3 | 4 | 5;
const BRACKETS = [
  {
    id: 1 as BracketId,
    title: "Bracket 1: Exhibition",
    experience:
      "Ultra-casual; show off something unusual. Games go long and end slowly.",
    deckBuilding:
      "No Game Changers. No intentional 2-card infinites, mass land denial, or extra-turn chains. Tutors sparse.",
  },
  {
    id: 2 as BracketId,
    title: "Bracket 2: Core",
    experience:
      "Average precon power. Splashy turns/engines; ~9+ turns; rarely ends out of nowhere.",
    deckBuilding:
      "No Game Changers. No intentional 2-card infinites/mass land denial. Extra turns few, not chained. Tutors sparse.",
  },
  {
    id: 3 as BracketId,
    title: "Bracket 3: Upgraded",
    experience:
      "Carefully tuned; a turn or two faster than Core. May use up to three Game Changers. Late finisher combos possible.",
    deckBuilding:
      "Up to three Game Changers. No early cheap 2-card infinites. Extra turns few, not looped. No mass land denial.",
  },
  {
    id: 4 as BracketId,
    title: "Bracket 4: Optimized",
    experience:
      "Strong non-tournament decks: explosive starts, tutors, cheap combos, land destruction, many Game Changers.",
    deckBuilding: "No extra restrictions beyond the banned list.",
  },
  {
    id: 5 as BracketId,
    title: "Bracket 5: cEDH",
    experience:
      "Competitive, metagame-aware mindset; deckbuilding/play prioritize winning; etiquette aligns with tournament norms.",
    deckBuilding: "No extra restrictions beyond the banned list.",
  },
];

/* ----------------------------------------
   Types
----------------------------------------- */
type Visibility = {
  display_name: boolean;
  bio: boolean;
  location: boolean;
  play_formats: boolean;
  meetup_types: boolean;
  competitive_preference: boolean;
  commander_tiers: boolean;
  play_radius: boolean;
  trade_radius: boolean;
  available_for_trading: boolean;
  available_for_play: boolean;
  collection_browsing: boolean;
  wishlist_is_public: boolean;
  preferred_lgs?: boolean;
};
type Preferences = {
  available_for_trading?: boolean;
  available_for_play?: boolean;
  collection_browsing?: boolean;
  wishlist_is_public?: boolean;
};
type Presence = {
  show_in_marketplace?: boolean;
  show_in_community_stream?: boolean;
};
type EmailPrefs = {
  offers?: boolean;
  follows?: boolean;
  dms?: boolean;
  marketing?: boolean;
};
type CompetitiveChoice = "casual" | "competitive" | "ranked";
type FollowPrivacy = "open" | "request" | "private";
type Profile = {
  username?: string | null;
  display_name?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  location?: { city?: string; state?: string; lat?: number | null; lng?: number | null };
  play_radius?: number;
  trade_radius?: number;
  play_formats?: string[];
  meetup_types?: string[];
  preferred_lgs?: string;
  competitive_preferences?: CompetitiveChoice[];
  competitive_preference?: CompetitiveChoice; // legacy single
  commander_brackets?: BracketId[] | null; // new multi
  commander_bracket?: BracketId | null; // legacy single
  preferences?: Preferences;
  presence?: Presence;
  follow_privacy?: FollowPrivacy;
  visibility?: Visibility;
  email_prefs?: EmailPrefs;
  createdAt?: Timestamp | FieldValue;
  updatedAt?: Timestamp | FieldValue;
};

/* ----------------------------------------
   Defaults
----------------------------------------- */
const defaultVisibility: Visibility = {
  display_name: true,
  bio: true,
  location: true,
  play_formats: true,
  meetup_types: true,
  competitive_preference: true,
  commander_tiers: true,
  play_radius: false,
  trade_radius: false,
  available_for_trading: true,
  available_for_play: true,
  collection_browsing: true,
  wishlist_is_public: false,
  preferred_lgs: true,
};
const defaultEmailPrefs: Required<EmailPrefs> = {
  offers: true,
  follows: true,
  dms: true,
  marketing: false,
};

// --- Constants (must appear before defaultProfile uses them) ---
const DEFAULT_AVATAR = "/images/avatars/avatar_default.png";

const PLAY_FORMATS = [
  "Commander",
  "cEDH",
  "Canadian Highlander",
  "Modern",
  "Standard",
  "Pioneer",
  "Legacy",
  "Vintage",
  "Pauper",
  "Draft",
  "Sealed",
  "MTG Arena",
  "Explorer",
  "Historic",
  "Alchemy",
  "D&D (non-MTG)",
];

const MEETUP_TYPES = ["Private Match", "Public Match", "Local Game Store"];
const COMPETITIVE_CHOICES: CompetitiveChoice[] = ["casual", "competitive", "ranked"];

/* ----------------------------------------
   Defaults
----------------------------------------- */
const defaultProfile: Profile = {
  username: "",
  display_name: "",
  bio: "",
  avatar_url: DEFAULT_AVATAR, // default avatar
  location: { city: "", state: "", lat: null, lng: null },
  play_radius: 10,
  trade_radius: 25,
  play_formats: [],
  meetup_types: [],
  preferred_lgs: "",
  competitive_preferences: [],
  competitive_preference: "casual",
  commander_brackets: [],
  commander_bracket: null,
  preferences: {
    available_for_trading: true,
    available_for_play: true,
    collection_browsing: true,
    wishlist_is_public: false,
  },
  presence: {
    show_in_marketplace: true,
    show_in_community_stream: true,
  },
  follow_privacy: "open",
  visibility: defaultVisibility,
  email_prefs: defaultEmailPrefs,
};

/* ----------------------------------------
   Preset Avatars
----------------------------------------- */
const PRESET_AVATARS: readonly string[] = [
  "/images/avatars/avatar_default.png",
  "/images/avatars/avatar2.png",
  "/images/avatars/avatar3.png",
  "/images/avatars/avatar4.png",
  "/images/avatars/avatar5.png",
  "/images/avatars/avatar6.png",
  "/images/avatars/avatar7.png",
  "/images/avatars/avatar8.png",
  "/images/avatars/avatar10.png",
  "/images/avatars/avatar11.png",
  "/images/avatars/avatar12.png",
  "/images/avatars/avatar13.png",
  "/images/avatars/avatar14.png",
  "/images/avatars/avatar15.png",
  "/images/avatars/avatar16.png",
  "/images/avatars/avatar17.png",
  "/images/avatars/avatar18.png",
  "/images/avatars/avatar19.png",
  "/images/avatars/avatar20.png",
  "/images/avatars/avatar21.png",
  "/images/avatars/avatar22.png",
  "/images/avatars/avatar23.png",
  "/images/avatars/avatar24.png",
  "/images/avatars/avatar25.png",
  "/images/avatars/avatar26.png",
  "/images/avatars/avatar27.png",
  "/images/avatars/avatar28.png",
];

/* ----------------------------------------
   Helpers (Wizards + OSM fallback)
----------------------------------------- */
// Default search radius (miles) used for the first LGS search
const lgsBaseRadius = 25; // ADDED

type WPNStore = {
  id?: string;
  name: string;
  lat: number;
  lng: number;
  address?: string;
  distanceKm?: number;
  provider?: "wizards" | "osm";
};

function kmDistance(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const R = 6371;
  const toRad = (n: number) => (n * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la = toRad(a.lat);
  const lb = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la) * Math.cos(lb) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

// ADD: basic filter for OSM/WPN names (avoid false positives; safe default true)
function passesMagicHeuristic(name: string, address?: string): boolean {
  const n = (name || "").toLowerCase();
  const a = (address || "").toLowerCase();
  if (!n) return false;
  // De-emphasize obvious non-stores; tweak as needed
  const bad = ["parking", "lot", "garage", "atm", "bank", "bus stop", "station"];
  if (bad.some((w) => n.includes(w) || a.includes(w))) return false;
  return true;
}

/** Read nearby LGS from lgs_public using geohash window and filter by distance. */
async function lgsNearbyFromPublic(
  lat: number,
  lng: number,
  radiusMiles: number,
  maxResults = 15
): Promise<WPNStore[]> {
  const radiusKm = Math.max(0.1, radiusMiles * 1.60934);
  const bounds = geohashQueryBounds([lat, lng], radiusKm * 1000);
  const seen = new Map<string, WPNStore>();

  for (const [start, end] of bounds) {
    const qRef = query(
      collection(db, "lgs_public"),
      orderBy("g"),
      startAt(start),
      endAt(end),
      limit(200)
    );
    const snap = await getDocs(qRef);
    snap.forEach((d) => {
      const v: any = d.data();
      const sLat = Number(v?.lat);
      const sLng = Number(v?.lng);
      if (!Number.isFinite(sLat) || !Number.isFinite(sLng)) return;
      const distKm = kmDistance({ lat, lon: lng }, { lat: sLat, lon: sLng });
      if (distKm <= radiusKm) {
        const key = d.id;
        if (!seen.has(key)) {
          seen.set(key, {
            id: d.id,
            name: String(v?.name ?? "Game Store"),
            address: v?.address ?? "",
            lat: sLat,
            lng: sLng,
            distanceKm: distKm,
            provider: "wizards",
          });
        }
      }
    });
  }

  return Array.from(seen.values())
    .sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0))
    .slice(0, maxResults);
}

// Modified to use lgs_public first, then WPN backfill if empty
async function fetchNearbyLgsPublicFirst(
  lat: number,
  lng: number,
  radiusMiles = lgsBaseRadius,
  maxResults = 60
): Promise<WPNStore[]> {
  let rows = await lgsNearbyFromPublic(lat, lng, radiusMiles, maxResults);
  if (rows.length === 0) {
    try {
      // Backfill from WPN and persist into lgs_public
      await tryWpnBackfill(lat, lng, Math.round(radiusMiles * 1609.34)); // meters
    } catch {}
    rows = await lgsNearbyFromPublic(lat, lng, radiusMiles, maxResults);
  }
  return rows;
}

/* ----------------------------------------
   Component
----------------------------------------- */
export default function Settings() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg] = useState<string | null>(null);

  // Local editable form state + dirty guard
  const [profile, _setProfile] = useState(defaultProfile);
  const [dirty, setDirty] = useState(false);
  const initializedRef = useRef(false);
  // Wrapper preserves your existing setProfile(...) calls and marks form dirty
  const setProfile = useCallback(
    (next: React.SetStateAction<Profile>) => {
      setDirty(true);
      if (typeof next === "function") {
        _setProfile((prev) => (next as (p: Profile) => Profile)(prev));
      } else {
        _setProfile(next as Profile);
      }
    },
    []
  );

  const [usernameInput, setUsernameInput] = useState("");

  const [autoLocationOnLogin, setAutoLocationOnLoginState] = useState<boolean>(false);

  const [searchParams, setSearchParams] = useSearchParams(); // get setter too
  const location = useLocation();
  const navigate = useNavigate();

  const [lgsUpgradeOpen, setLgsUpgradeOpen] = useState(false);
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;

    const q = (searchParams.get("modal") || "").toLowerCase();
    const fromState = Boolean((location.state as any)?.lgsUpgrade);

    if (q === "lgsupgrade" || fromState) {
      openedRef.current = true;
      setLgsUpgradeOpen(true);

      // Clean query + state so reload/back doesn’t auto-open again
      const sp = new URLSearchParams(searchParams);
      sp.delete("modal");
      navigate(
        { pathname: location.pathname, search: sp.toString() ? `?${sp}` : "" },
        { replace: true, state: {} }
      );
    }
  }, [searchParams, location.pathname, location.state, navigate]);

  // When modal closes, also ensure URL stays clean
  const onLgsOpenChange = (open: boolean) => {
    setLgsUpgradeOpen(open);
    if (!open) {
      const sp = new URLSearchParams(searchParams);
      if (sp.get("modal")) {
        sp.delete("modal");
        navigate(
          { pathname: location.pathname, search: sp.toString() ? `?${sp}` : "" },
          { replace: true }
        );
      }
    }
  };

  // --- Modal close guard (ESC) ---
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLgsUpgradeOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Open LGS modal from URL, but clean the URL FIRST so it never traps the page.
  React.useEffect(() => {
    if (openedRef.current) return;
    const q = (searchParams.get("lgs") || "").toLowerCase();
    const hash = (typeof window !== "undefined" ? window.location.hash : "").toLowerCase();
    const shouldOpen =
      (q && q !== "0" && q !== "false") || hash === "#lgs";

    if (!shouldOpen) return;

    // Clean query so refresh/back won’t re-open
    const next = new URLSearchParams(searchParams);
    next.delete("lgs");
    setSearchParams(next, { replace: true });

    // Clean hash if present
    if (hash === "#lgs") {
      try {
        const { pathname, search } = window.location;
        window.history.replaceState(null, "", pathname + search);
      } catch {}
    }

    openedRef.current = true;
    setLgsUpgradeOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // do not include lgsOpen; we just react to URL once

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const pref = await getAutoLocationOnLogin();
        if (alive) setAutoLocationOnLoginState(pref);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function handleToggleAutoLocation(next: boolean) {
    setAutoLocationOnLoginState(next);
    try {
      void setAutoLocationOnLogin(next);
      toast({ description: next ? "Will use current location on login" : "Will prompt on login" });
    } catch {
      setAutoLocationOnLoginState((v) => !v);
      toast({ description: "Failed to save preference" });
    } finally {
      setTimeout(() => toast({ description: null }), 1400);
    }
  }

  /* ===== LGS SEARCH STATE (REFACTORED FOR STREAM + PAGINATION) ===== */
  const [lgsLoading, setLgsLoading] = useState(false);
  const [lgsAll, setLgsAll] = useState<WPNStore[]>([]);
  const [lgsQuery, setLgsQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(15); // reserved for future
  const [lgsCurrentRadius, setLgsCurrentRadius] = useState<number | null>(null); // miles used in last fetch
  const [lgsPage, setLgsPage] = useState(0);                    // current page (0-based)
  const LGS_PAGE_SIZE = 15;
  const [showPagination, setShowPagination] = useState(false);  // becomes true after first "Load more"
  const lgsAbortRef = useRef<{ wpn?: AbortController } | null>(null);

  // Filter all results first
  const filteredAllLgs = useMemo(() => {
    const q = lgsQuery.trim().toLowerCase();
    if (!q) return lgsAll;
    return lgsAll.filter((s) => {
      const name = s.name.toLowerCase();
      const addr = (s.address || "").toLowerCase();
      return name.includes(q) || addr.includes(q);
    });
  }, [lgsAll, lgsQuery]);

  // Decide which slice to display
  const displayLgs = useMemo(() => {
    if (!showPagination) {
      return filteredAllLgs.slice(0, LGS_PAGE_SIZE);
    }
    const start = lgsPage * LGS_PAGE_SIZE;
    return filteredAllLgs.slice(start, start + LGS_PAGE_SIZE);
  }, [filteredAllLgs, showPagination, lgsPage]);

  const totalPages = useMemo(
    () => Math.ceil(filteredAllLgs.length / LGS_PAGE_SIZE),
    [filteredAllLgs.length]
  );

  // Keep page in range when filter shrinks result set
  useEffect(() => {
    if (showPagination && lgsPage >= totalPages) {
      setLgsPage(0);
    }
  }, [showPagination, lgsPage, totalPages]);

  // Avatar chooser
  const [chooserOpen, setChooserOpen] = useState(false);
  const avatarWrapRef = useRef<HTMLDivElement | null>(null);
  const chooserRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // --- Minimal cropper state ---
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropScale, setCropScale] = useState(1);
  const [baseScale, setBaseScale] = useState(1);
  const [cropPos, setCropPos] = useState({ x: 0, y: 0 });
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef({ active: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const CROP_PREVIEW_SIZE = 320;
  const CROP_CANVAS_SIZE = 512;

  function resetCropper(): void {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropOpen(false);
    setCropSrc(null);
    setCropScale(1);
    setBaseScale(1);
    setCropPos({ x: 0, y: 0 });
  }

  function onCropImgLoad(e: React.SyntheticEvent<HTMLImageElement>): void {
    const img = e.currentTarget;
    const iw = img.naturalWidth || 1;
    const ih = img.naturalHeight || 1;
    const fit = Math.max(CROP_PREVIEW_SIZE / iw, CROP_PREVIEW_SIZE / ih);
    setBaseScale(fit);
    setCropScale(1);
    setCropPos({ x: 0, y: 0 });
  }

  function onStartDrag(e: React.PointerEvent<HTMLDivElement>): void {
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      active: true,
      sx: e.clientX,
      sy: e.clientY,
      ox: cropPos.x,
      oy: cropPos.y,
    };
  }
  function onDragMove(e: React.PointerEvent<HTMLDivElement>): void {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.sx;
    const dy = e.clientY - dragRef.current.sy;
    setCropPos({ x: dragRef.current.ox + dx, y: dragRef.current.oy + dy });
  }
  function onEndDrag(e: React.PointerEvent<HTMLDivElement>): void {
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {}
    dragRef.current.active = false;
  }

  async function uploadCroppedBlob(blob: Blob): Promise<void> {
    if (!user) return;
    const path = `avatars/${user.uid}/avatar.png`;
    const fileRef = sRef(storage, path);
    await uploadBytes(fileRef, blob, {
      contentType: "image/png",
      cacheControl: "public, max-age=3600",
    });
    const url = await getDownloadURL(fileRef);
    await updateDoc(doc(db, "users", user.uid), {
      avatar_url: url,
      updatedAt: serverTimestamp(),
    });
    toast({ description: "Profile image updated" });
    setTimeout(() => toast({ description: null }), 1600);
    setChooserOpen(false);
  }

  async function onConfirmCrop(): Promise<void> {
    const img = imgRef.current;
    if (!img || !cropSrc) return;
    const canvas = document.createElement("canvas");
    canvas.width = CROP_CANVAS_SIZE;
    canvas.height = CROP_CANVAS_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const scale = baseScale * cropScale;
    const iw = img.naturalWidth || 1;
    const ih = img.naturalHeight || 1;
    const sw = iw * scale;
    const sh = ih * scale;

    const tlx = CROP_PREVIEW_SIZE / 2 - sw / 2 + cropPos.x;
    const tly = CROP_PREVIEW_SIZE / 2 - sh / 2 + cropPos.y;

    const k = CROP_CANVAS_SIZE / CROP_PREVIEW_SIZE;
    const dx = tlx * k;
    const dy = tly * k;
    const dw = sw * k;
    const dh = sh * k;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, dx, dy, dw, dh);

    canvas.toBlob(async (blob) => {
      if (blob) {
        await uploadCroppedBlob(blob);
      }
      resetCropper();
    }, "image/png", 0.95);
  }

  const [newEmail, setNewEmail] = useState<string>("");
  const [currentPw, setCurrentPw] = useState<string>("");
  const [newPw, setNewPw] = useState<string>("");
  const [confirmPw, setConfirmPw] = useState<string>("");

  const hasPasswordProvider = !!user?.providerData?.some((p) => p.providerId === "password");

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent): void {
      if (!chooserOpen) return;
      const t = e.target as Node | null;
      const inAnchor = avatarWrapRef.current?.contains(t ?? null) ?? false;
      const inChooser = chooserRef.current?.contains(t ?? null) ?? false;
      if (!inAnchor && !inChooser) setChooserOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [chooserOpen]);

  useEffect(() => {
    let unsubProfile: (() => void) | null = null;
    let cancelled = false;

    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) {
        setLoading(false);
        unsubProfile?.();
        return;
      }
      try {
        const ref = doc(db, "users", u.uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          await setDoc(
            ref,
            {
              ...defaultProfile,
              username: "",
              display_name: u.displayName ?? "",
              avatar_url: DEFAULT_AVATAR,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          );
        } else {
          const cur = snap.data() as any;
          if (!cur?.avatar_url || String(cur.avatar_url).trim() === "") {
            await updateDoc(ref, {
              avatar_url: DEFAULT_AVATAR,
              updatedAt: serverTimestamp(),
            });
          }
        }
        if (cancelled) return;
        unsubProfile = onSnapshot(ref, (s) => {
          const data = (s.data() ?? {}) as Profile & { locationPreference?: { autoOnLogin?: boolean } };

          const derivedBrackets: BracketId[] =
            Array.isArray(data.commander_brackets) && data.commander_brackets.length
              ? (data.commander_brackets as BracketId[])
              : data.commander_bracket
                ? [data.commander_bracket as BracketId]
                : [];

          const derivedComp: CompetitiveChoice[] =
            Array.isArray(data.competitive_preferences) && data.competitive_preferences.length
              ? (data.competitive_preferences as CompetitiveChoice[])
              : data.competitive_preference
                ? [data.competitive_preference as CompetitiveChoice]
                : [];

          const avatarUrl =
            (data.avatar_url && String(data.avatar_url).trim())
              ? (data.avatar_url as string)
              : DEFAULT_AVATAR;

          // Do not override local edits while dirty; initialize once
          if (initializedRef.current && dirty) {
            return;
          }

          _setProfile({
            ...defaultProfile,
            ...data,
            commander_brackets: derivedBrackets,
            commander_bracket: (data.commander_bracket as BracketId | null) ?? null,
            competitive_preferences: derivedComp,
            competitive_preference:
              (data.competitive_preference as CompetitiveChoice) ?? "casual",
            location: { ...defaultProfile.location, ...(data.location ?? {}) },
            preferences: { ...defaultProfile.preferences, ...(data.preferences ?? {}) },
            presence: { ...defaultProfile.presence, ...(data.presence ?? {}) },
            visibility: { ...defaultVisibility, ...(data.visibility ?? {}) },
            email_prefs: { ...defaultEmailPrefs, ...(data.email_prefs ?? {}) },
            avatar_url: avatarUrl,
          });

          if (!initializedRef.current) {
            setAutoLocationOnLogin(Boolean(data?.locationPreference?.autoOnLogin));
            setUsernameInput((data.username ?? "") || "");
            setNewEmail(u.email ?? "");
            initializedRef.current = true;
          }
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubAuth();
      unsubProfile?.();
    };
  }, [dirty]);

  const trade = Boolean(profile.preferences.available_for_trading);
  const play = Boolean(profile.preferences.available_for_play);
  const mode = useMemo(
    () =>
      trade && play
        ? "All settings enabled"
        : trade && !play
          ? "Trade-only: Only Meetup Options & Preferred LGS are editable"
          : !trade && play
            ? "Play-only: Everything except Meetup Options & Preferred LGS is editable"
            : "All disabled: Toggle options to begin",
    [trade, play],
  );

  function onPickFile(): void {
    fileInputRef.current?.click();
  }

  const onUploadAvatar = useCallback(async (file: File): Promise<void> => {
    if (!user) return;
    const ext = (file.name.split(".").pop() || "png").toLowerCase();
    const path = `avatars/${user.uid}/avatar.${ext}`;
    const fileRef = sRef(storage, path);
    await uploadBytes(fileRef, file, {
      contentType: file.type,
      cacheControl: "public, max-age=3600",
    });
    const url = await getDownloadURL(fileRef);
    await updateDoc(doc(db, "users", user.uid), { avatar_url: url, updatedAt: serverTimestamp() });
    pushToast("Profile image updated", 1600);
    setChooserOpen(false);
  }, [user]);

  const onChoosePreset = useCallback(async (src: string): Promise<void> => {
    if (!user) return;
    await updateDoc(doc(db, "users", user.uid), { avatar_url: src, updatedAt: serverTimestamp() });
    pushToast("Avatar changed", 1400);
    setChooserOpen(false);
  }, [user]);

  const usernameLocked = Boolean(profile.username && profile.username.trim().length > 0);

  async function onSetUsername(): Promise<void> {
    if (!user) {
      pushToast("Sign in first", 1700);
      return;
    }
    try {
      const normalized = normalizeUsername(usernameInput);
      if (!normalized) {
        pushToast("Pick a valid username", 1800);
        return;
      }
      const u = await claimUsername(user.uid, normalized);
      await updateDoc(doc(db, "users", user.uid), { username: u, updatedAt: serverTimestamp() });
      pushToast(`Username set to @${u}`, 2500);
    } catch (e: unknown) {
      pushToast(e instanceof Error ? e.message : "Could not set username", 2500);
    }
  }

  const publicUrl: string | null = useMemo(() => {
    if (!profile.username) return null;
    const origin =
      typeof window !== "undefined" && window.location ? window.location.origin : "";
    return `${origin}/${encodeURIComponent(profile.username)}`;
  }, [profile.username]);

  const shareOrCopyProfile = useCallback(async () => {
    if (!publicUrl) {
      pushToast("Set a username to share your profile.", 1800);
    } else {
      try {
        if (navigator.share) {
          await navigator.share({ title: `${profile.username} on Spellgather`, url: publicUrl });
          pushToast("Shared", 1200);
          return;
        }
      } catch { /* ignore */ }
      try {
        await navigator.clipboard.writeText(publicUrl);
        pushToast("Link copied", 1400);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = publicUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        pushToast("Link copied", 1400);
      }
    }
  }, [publicUrl, profile.username]);

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      pushToast("Geolocation not supported in this browser.", 2000);
      return;
    }
    pushToast("Getting location…", 2000);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setProfile((p) => ({ ...p, location: { ...(p.location ?? {}), lat: latitude, lng: longitude } }));
        pushToast("Location set ✓", 1200);
        if (profile.preferences.available_for_trading) {
          void findNearbyLGS(latitude, longitude, 100);
        }
      },
      (err) => {
        pushToast(
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied."
            : "Could not get location.",
          2500
        );
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [profile.preferences.available_for_trading, setProfile]);

  // Modified to return rows
  async function findNearbyLGS(
    lat: number,
    lng: number,
    radiusMiles = 100,
    opts: { silent?: boolean } = {}
  ): Promise<void> {
    // Abort any in-flight WPN fetch
    try { lgsAbortRef.current?.wpn?.abort(); } catch {}
    const wpnCtrl = new AbortController();
    lgsAbortRef.current = { wpn: wpnCtrl };

    // First search or radius expansion
    const initial = lgsCurrentRadius == null;
    if (initial) {
      setLgsAll([]);          // fresh search
      setShowPagination(false);
      setLgsPage(0);
    }

    setLgsLoading(true);
    setLgsCurrentRadius(radiusMiles);

    // When each source returns, merge immediately (stream-like)
    const addRows = (rows: WPNStore[]) => {
      if (!rows || !rows.length) return;
      setLgsAll((prev) => mergeLgs(prev, rows));
    };

    // 1) Public DB first (fast), possibly after prior backfill
    try {
      const publicRows = await fetchNearbyLgsPublicFirst(lat, lng, radiusMiles, 60);
      addRows(publicRows);
    } catch {}

    // 2) Trigger WPN backfill again for expanded radius (then re-read), 3) OSM in parallel
    const wpnP = (async () => {
      try {
        await tryWpnBackfill(lat, lng, Math.round(radiusMiles * 1609.34), { signal: wpnCtrl.signal as any });
      } catch {}
      try {
        const rerows = await lgsNearbyFromPublic(lat, lng, radiusMiles, 60);
        addRows(rerows);
      } catch {}
    })();

    const osmP = searchLgsOSM(lat, lng, radiusMiles)
      .then((rows: any[]) => {
        const mapped: WPNStore[] = (rows || []).map((s: any) => ({
          id: s.id,
          name: s.name,
          address: s.address,
          lat: s.lat,
          lng: s.lng,
          distanceKm:
            typeof s.distanceKm === "number"
              ? s.distanceKm
              : kmDistance({ lat, lon: lng }, { lat: s.lat, lon: s.lng }),
          provider: "osm",
        }));
        addRows(mapped.filter((r) => passesMagicHeuristic(r.name, r.address)));
      })
      .catch(() => {});

    await Promise.allSettled([wpnP, osmP]);
    setLgsLoading(false);

    if (!opts.silent) {
      setTimeout(() => {
        pushToast("Nearby store search complete.", 1400);
      }, 250);
    }
  }

  /* ===== MERGE HELPER (reused) ===== */
  function mergeLgs(existing: WPNStore[], incoming: WPNStore[]): WPNStore[] {
    if (!incoming.length) return existing;
    const key = (r: WPNStore) =>
      `${r.name.toLowerCase()}|${r.lat.toFixed(4)}|${r.lng.toFixed(4)}`;
    const map = new Map<string, WPNStore>();
    for (const r of existing) map.set(key(r), r);
    for (const r of incoming) {
      const k = key(r);
      if (!map.has(k)) map.set(k, r);
    }
    return Array.from(map.values()).sort(
      (a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0)
    );
  }

  const handleLgsSearch = useCallback(async () => {
    const lat = profile.location?.lat;
    const lng = profile.location?.lng;
    if (typeof lat !== "number" || typeof lng !== "number") {
      pushToast("Set your location first.", 1800);
      return;
    }
    const nextRadius = lgsCurrentRadius == null ? lgsBaseRadius : lgsCurrentRadius + 5;
    await findNearbyLGS(lat, lng, nextRadius);
  }, [profile.location?.lat, profile.location?.lng, lgsCurrentRadius]);

  const saveLgsSelection = useCallback(async (s: WPNStore): Promise<void> => {
    if (!user) return;
    try {
      const idBase = slugify(`${s.name}-${s.address || ""}-${s.lat.toFixed(4)}-${s.lng.toFixed(4)}`);
      const lgsRef = doc(db, "lgs", idBase);
      try {
        await setDoc(
          lgsRef,
          {
            name: s.name,
            address: s.address || "",
            lat: s.lat,
            lng: s.lng,
            source: s.id ? "wizards" : "osm",
            external_id: s.id || null,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            createdBy: user.uid,
          },
          { merge: true }
        );
      } catch (err: any) {
        if (err?.code !== "permission-denied") console.warn("LGS write failed:", err);
      }
      await updateDoc(doc(db, "users", user.uid), {
        preferred_lgs: s.name,
        updatedAt: serverTimestamp(),
      });
      pushToast(`Preferred LGS set to “${s.name}”`, 1400);
    } catch (err: any) {
      console.error(err);
      pushToast(err?.message ?? "Could not save store", 2000);
    }
  }, [user]);

  // Non-optimistic save: show “Saving…” toast, await write, then notify
  const onSave = useCallback(async () => {
    if (!user) return;
    setSaving(true);
    // ADD: saving toast (non-optimistic)
    pushToast("Saving...you can navigate away at this time. We will notify when save is complete.", 4000);
    try {
      const ref = doc(db, "users", user.uid);

      const presence: Presence = {
        show_in_marketplace: Boolean(profile.preferences.available_for_trading),
        show_in_community_stream: Boolean(profile.preferences.available_for_play),
      };

      const loc = {
        city: profile.location?.city ?? "",
        state: profile.location?.state ?? "",
        lat: profile.location?.lat ?? null,
        lng: profile.location?.lng ?? null,
      };

      const base: Partial<Profile> = {
        location: loc,
        preferences: {
          ...profile.preferences,
          available_for_trading: profile.preferences.available_for_trading,
          available_for_play: profile.preferences.available_for_play,
        },
        presence,
        follow_privacy: profile.follow_privacy,
        visibility: profile.visibility,
        email_prefs: {
          offers: !!profile.email_prefs?.offers,
          follows: !!profile.email_prefs?.follows,
          dms: !!profile.email_prefs?.dms,
          marketing: !!profile.email_prefs?.marketing,
        },
        updatedAt: serverTimestamp(),
      };

      const otherPatch: Partial<Profile> = profile.preferences.available_for_play
        ? {
            display_name: profile.display_name,
            bio: profile.bio,
            play_radius: profile.play_radius,
            trade_radius: profile.trade_radius,
            play_formats: profile.play_formats,
            commander_brackets: profile.commander_brackets ?? [],
            commander_bracket: profile.commander_brackets?.[0] ?? null,
            competitive_preferences: profile.competitive_preferences ?? [],
            competitive_preference:
              (profile.competitive_preferences?.[0] as CompetitiveChoice) ?? "casual",
            preferences: {
              ...profile.preferences,
              collection_browsing: profile.preferences.collection_browsing,
              wishlist_is_public: profile.preferences.wishlist_is_public,
            },
          }
        : { bio: profile.bio };

      const meetupPatch: Partial<Profile> = profile.preferences.available_for_trading
        ? { meetup_types: profile.meetup_types, preferred_lgs: profile.preferred_lgs }
        : {};

      await updateDoc(ref, { ...base, ...otherPatch, ...meetupPatch });

      // Clear dirty so next Firestore snapshot can refresh the view
      setDirty(false);

      pushToast("Settings saved.", 1500);
    } catch (e: any) {
      pushToast(e?.message ?? "Save failed", 2500);
    } finally {
      setSaving(false);
    }
  }, [user, profile]);

  async function ensureRecentLoginForEmail(): Promise<boolean> {
    if (!user) return false;
    try {
      await updateEmail(user, newEmail.trim());
      return true;
    } catch (err: any) {
      if (err?.code === "auth/requires-recent-login") {
        if (hasPasswordProvider && user.email) {
          const pw =
            currentPw ||
            window.prompt("Please enter your current password to confirm email change") ||
            "";
          if (!pw) return false;
          try {
            const cred = EmailAuthProvider.credential(user.email, pw);
            await reauthenticateWithCredential(user, cred);
            await updateEmail(user, newEmail.trim());
            return true;
          } catch {
            toast({ description: "Reauthentication failed. Try again." });
            setTimeout(() => toast({ description: null }), 2200);
            return false;
          }
        } else {
          toast({ description: "Please sign out and sign back in to update your email." });
          setTimeout(() => toast({ description: null }), 2600);
          return false;
        }
      } else {
        throw err;
      }
    }
  }

  async function onUpdateEmail(): Promise<void> {
    if (!user) return;
    const target = newEmail.trim();
    if (!target) {
      toast({ description: "Enter a valid email." });
      setTimeout(() => toast({ description: null }), 1600);
      return;
    }
    try {
      const ok = await ensureRecentLoginForEmail();
      if (!ok) return;
      pushToast("Email updated ✓", 2000);
    } catch (e: any) {
      pushToast(e?.message ?? "Email update failed", 2200);
    }
  }

  async function onSendVerify(): Promise<void> {
    if (!user) return;
    try {
      await sendEmailVerification(user);
      pushToast("Verification email sent.", 2200);
    } catch (e: any) {
      pushToast(e?.message ?? "Failed to send verification", 2200);
    }
  }

  async function onUpdatePassword(): Promise<void> {
    if (!user) return;
    if (!hasPasswordProvider) {
      toast({ description: "Password changes are only for email/password accounts." });
      setTimeout(() => toast({ description: null }), 2400);
      return;
    }
    if (!currentPw) {
      toast({ description: "Enter your current password to continue." });
      setTimeout(() => toast({ description: null }), 2000);
      return;
    }
    if (!newPw || newPw.length < 6) {
      toast({ description: "New password must be at least 6 characters." });
      setTimeout(() => toast({ description: null }), 2000);
      return;
    }
    if (newPw !== confirmPw) {
      toast({ description: "New password and confirmation do not match." });
      setTimeout(() => toast({ description: null }), 2000);
      return;
    }
    try {
      if (user.email) {
        const cred = EmailAuthProvider.credential(user.email, currentPw);
        await reauthenticateWithCredential(user, cred);
      }
      await updatePassword(user, newPw);
      pushToast("Password updated ✓", 2200);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (e: any) {
      pushToast(e?.message ?? "Password update failed", 2200);
    }
  }

  // Warn on page unload if coords are missing
  useEffect(() => {
    const missing = profile.location?.lat == null || profile.location?.lng == null;
    if (!missing) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [profile.location?.lat, profile.location?.lng]);

  if (loading) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <p className="text-white">Loading…</p>
      </main>
    );
  }
  if (!user) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <Card className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white border-slate-700 shadow-lg">
          <CardHeader>
            <CardTitle>Settings</CardTitle>
            <CardDescription className="text-white/90">
              Sign in to manage your profile.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p>Use the Sign In on the home page.</p>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <>
      <main className="mx-auto max-w-3xl p-6 space-y-6">
       {(profile.location?.lat == null || profile.location?.lng == null) && (
         <div className="rounded-lg border border-sky-800 bg-sky-900/20 p-3 text-sky-100">
           <div className="font-semibold">Enable location for the best experience</div>
           <p className="text-sm mt-1">
             Setting your approximate location helps us show nearby players, local listings, store recommendations,
             and play opportunities. We store coordinates in an obfuscated form and use them only to power these features.
             You can turn off location-based options any time by toggling “Trade” and/or “Play”.
           </p>
           <div className="mt-2 flex gap-2">
             <Button variant="outline" onClick={useMyLocation} aria-label="Use my location">Use my location</Button>
             <Button
               variant="ghost"
               onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
               aria-label="Learn more"
             >
               Learn more
             </Button>
           </div>
         </div>
       )}
        {/* === PROFILE === */}
        <Card className="text-white border-purple-500/30 shadow-xl bg-gradient-to-br from-[#1b0f2e] via-[#160e27] to-[#120a1f]">
          <CardHeader className="px-6 py-4 bg-[#2a1650] text-amber-300 border-b border-amber-500/30 rounded-t-xl">
            <CardTitle className="text-amber-300">Profile</CardTitle>
            <CardDescription className="text-amber-200">
             Your avatar, username, and bio are always editable (usernames lock after selection).
           </CardDescription>
         </CardHeader>
        <CardContent className="space-y-6 px-6 py-6">
          <div className="flex items-start gap-4 sm:gap-6">
            {/* Avatar */}
            <div className="relative inline-block" ref={avatarWrapRef}>
              <button
                type="button"
                onClick={() => setChooserOpen((o) => !o)}
                className="group relative block rounded-xl overflow-hidden border border-purple-400/40 bg-black/30 shadow-md focus:outline-none focus:ring-2 focus:ring-purple-300/40"
                aria-label="Change profile image"
                style={{ width: 128, height: 128 }}
              >
                <div className="absolute inset-0 ring-1 ring-purple-300/20 rounded-xl pointer-events-none" />
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                    alt="Profile"
                  />
                ) : (
                  <div className="h-full w-full grid place-items-center text-sm text-white/70">
                    No image
                  </div>
                )}
                <span className="pointer-events-none absolute bottom-1 right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white/90 text-black shadow group-hover:scale-105 transition">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                    <path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zm14.71-9.21a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
                  </svg>
                </span>
              </button>

              {chooserOpen && (
                <div
                  ref={chooserRef}
                  className="absolute left-0 top-full mt-2 w-[min(92vw,520px)] rounded-xl border border-gray-700 bg-gray-900 shadow-xl p-3 z-20"
                >
                  <div className="text-white mb-2">Choose an avatar</div>
                  <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-7 gap-3">
                    <button
                      type="button"
                      onClick={onPickFile}
                      className="aspect-square rounded-xl border border-gray-700 bg-black/30 hover:bg-black/50 grid place-items-center"
                      aria-label="Upload avatar"
                      title="Upload avatar"
                    >
                      <div className="h-12 w-12 rounded-full bg-gray-700 grid place-items-center">
                        <span className="text-3xl leading-none text-white">＋</span>
                      </div>
                    </button>

                    {PRESET_AVATARS.map((src) => {
                      const active = profile.avatar_url === src;
                      return (
                        <button
                          key={src}
                          type="button"
                          onClick={() => onChoosePreset(src)}
                          className={[
                            "aspect-square rounded-xl overflow-hidden border transition focus:outline-none focus:ring-2 focus:ring-white/30",
                            active ? "border-emerald-400 ring-2 ring-emerald-400/40" : "border-gray-700 hover:border-gray-500",
                          ].join(" ")}
                          aria-label="Choose avatar"
                          title="Choose avatar"
                        >
                          <img src={src} alt="Avatar option" className="h-full w-full object-cover" />
                        </button>
                      );
                    })}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        try {
                          if (cropSrc) URL.revokeObjectURL(cropSrc);
                        } catch {}
                        setCropSrc(URL.createObjectURL(f));
                        setCropOpen(true);
                      }
                    }}
                  />
                </div>
              )}
            </div>

            {/* Username + Bio */}
            <div className="flex-1 min-w-0 space-y-4">
              <section className="grid gap-2">
                <label htmlFor="username-field" className="font-medium">
                  Username
                </label>
                <div className="flex gap-2">
                  <input
                    id="username-field"
                    className={["border rounded px-3 py-2 text-black flex-1", usernameLocked ? "opacity-60 cursor-not-allowed" : ""].join(" ")}
                    value={usernameLocked ? (profile.username ?? "") : usernameInput}
                    onChange={(e) => setUsernameInput(e.target.value)}
                    placeholder={profile.username ? profile.username : "choose-a-name"}
                    readOnly={usernameLocked}
                    disabled={usernameLocked}
                  />
                  <Button onClick={onSetUsername} disabled={usernameLocked} aria-label="Set username">
                    {usernameLocked ? "Locked" : "Set"}
                  </Button>
                </div>
                {profile.username && <p className="text-sm text-white/90">Current: @{profile.username}</p>}
              </section>

              <section className="grid gap-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="bio-field" className="font-medium">
                    Bio
                  </label>
                  <Switch
                    checked={profile.visibility.bio}
                    onChange={(v) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, bio: v } }))}
                    label="Show bio on profile"
                  />
                </div>
                <textarea
                  id="bio-field"
                  className="border rounded px-3 py-2 text-black"
                  value={profile.bio ?? ""}
                  onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))}
                  rows={3}
                  placeholder="Mage. Relic hoarder. Loves 16-bit sprites."
                />
              </section>

              <section className="flex items-center gap-3 flex-wrap">
                <Button variant="outline" onClick={shareOrCopyProfile} aria-label="Share public profile">
                  {navigator.share ? "Share / Copy profile link" : "Copy profile link"}
                </Button>
                <div className="text-sm text-white/90 break-all">
                  {publicUrl ? publicUrl : "Set a username to get your public link"}
                </div>
              </section>

              <section>
                <details className="group rounded-lg border border-gray-800 bg.black/20 bg-black/20">
                  <summary className="list-none cursor-pointer select-none px-3 py-2 flex items-center justify-between">
                    <span className="text-sm font-semibold">Account: Email & Password</span>
                    <span className="text-xs opacity-80 group-open:rotate-180 transition-transform">▾</span>
                  </summary>

                  <div className="px-3 pb-3 space-y-6">
                    <div className="grid gap-2">
                      <label htmlFor="email-field" className="font-medium">
                        Email address
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="email-field"
                          type="email"
                          className="border rounded px-3 py-2 text-black flex-1"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="you@example.com"
                          autoComplete="email"
                        />
                        <Button onClick={onUpdateEmail} aria-label="Update email">Update</Button>
                      </div>
                      <div className="text-xs text.white/90 text-white/90">
                        Current: {user.email ?? "—"} {user.emailVerified ? "• Verified" : "• Not verified"}{" "}
                        {!user.emailVerified && (
                          <button className="underline hover:opacity-80 ml-1" onClick={onSendVerify} aria-label="Send verification">
                            Send verification
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <label className="font-medium">Password</label>
                        {!hasPasswordProvider && (
                          <span className="text-xs text-white/80">
                            Your account uses a social provider. Password changes are disabled.
                          </span>
                        )}
                      </div>
                      <div className="grid sm:grid-cols-3 gap-2">
                        <input
                          type="password"
                          placeholder="Current password"
                          className="border rounded px-3 py-2 text-black"
                          value={currentPw}
                          onChange={(e) => setCurrentPw(e.target.value)}
                          disabled={!hasPasswordProvider}
                          autoComplete="current-password"
                        />
                        <input
                          type="password"
                          placeholder="New password"
                          className="border rounded px-3 py-2 text-black"
                          value={newPw}
                          onChange={(e) => setNewPw(e.target.value)}
                          disabled={!hasPasswordProvider}
                          autoComplete="new-password"
                        />
                        <input
                          type="password"
                          placeholder="Confirm new password"
                          className="border rounded px-3 py-2 text-black"
                          value={confirmPw}
                          onChange={(e) => setConfirmPw(e.target.value)}
                          disabled={!hasPasswordProvider}
                          autoComplete="new-password"
                        />
                      </div>
                      <div>
                        <Button onClick={onUpdatePassword} disabled={!hasPasswordProvider} aria-label="Update password">
                          Update password
                        </Button>
                      </div>
                    </div>
                  </div>
                </details>
              </section>
            </div>
          </div>
          </CardContent>
        </Card>

      {cropOpen && cropSrc && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" onClick={resetCropper}>
          <div className="absolute inset-0 bg-black/70" aria-hidden />
          <div className="absolute inset-0 grid place-items-center md:p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative w-full h-[100dvh] md:h-auto md:max-w-xl overflow-hidden md:rounded-xl rounded-none border border-gray-700 bg-gray-900 shadow-2xl text-white flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
              <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-800 bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-gray-900/60">
                <h3 className="font-semibold">Position your avatar</h3>
                <button className="btn-pixel btn-pixel-secondary" onClick={resetCropper} aria-label="Close">✕</button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                <div
                  className="relative mx-auto rounded-lg overflow-hidden border border-gray-700 bg-black/50"
                  style={{ width: CROP_PREVIEW_SIZE, height: CROP_PREVIEW_SIZE, touchAction: "none" }}
                  onPointerDown={onStartDrag}
                  onPointerMove={onDragMove}
                  onPointerUp={onEndDrag}
                  onPointerLeave={onEndDrag}
                >
                  <img
                    ref={imgRef}
                    src={cropSrc}
                    alt="Crop source"
                    draggable={false}
                    onLoad={onCropImgLoad}
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: `translate(-50%, -50%) translate(${cropPos.x}px, ${cropPos.y}px) scale(${baseScale * cropScale})`,
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  />
                  <div className="pointer-events-none absolute inset-0 ring-1 ring-white/15" />
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <label className="text-sm opacity-80">Zoom</label>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={cropScale}
                    onChange={(e) => setCropScale(parseFloat(e.target.value))}
                    className="flex-1"
                    aria-label="Zoom"
                  />
                  <Button variant="outline" onClick={() => setCropPos({ x: 0, y: 0 })}>
                    Center
                  </Button>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 p-4 border-t border-gray-800 bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-gray-900/60 flex justify-end gap-2">
                <Button variant="outline" onClick={resetCropper}>Cancel</Button>
                <Button onClick={onConfirmCrop}>Use image</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === AVAILABILITY === */}
      <Card className="text-white border-purple-500/30 shadow-lg bg-gradient-to-br from-[#1b0f2e] via-[#160e27] to-[#120a1f]">
        <CardHeader className="px-6 py-4 bg-[#2a1650] text-amber-300 border-b border-amber-500/30 rounded-t-xl text-center">
          <CardTitle className="text-amber-300 text-2xl">Availability</CardTitle>
          <CardDescription className="text-amber-200 text-base">
            Choose what you’re open for. These also control where you appear.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 px-6 py-6">
          <div className="grid sm:grid-cols-2 gap-4 place-items-stretch">
            <button
              type="button"
              aria-pressed={trade}
              onClick={() =>
                setProfile((p) => ({
                  ...p,
                  preferences: { ...p.preferences, available_for_trading: !trade },
                  presence: { ...(p.presence ?? {}), show_in_marketplace: !trade },
                }))
              }
              className={[
                "rounded-xl border p-4 text-left transition",
                "flex flex-col justify-center",
                trade
                  ? "bg-blue-600/30 border-blue-400 ring-1 ring-blue-300 shadow-inner"
                  : "bg-blue-900/10 border-blue-800 hover:bg-blue-900/20",
              ].join(" ")}
              title="Toggle Available for Trade"
            >
              <div className="text-xl font-bold text-purple-300">Available for Trade</div>
              <div className="text-sm opacity-90 mt-1">
                Enables Marketplace; when Trade-only, only Meetup Options & Preferred LGS are editable.
              </div>
            </button>

            <button
              type="button"
              aria-pressed={play}
              onClick={() =>
                setProfile((p) => ({
                  ...p,
                  preferences: { ...p.preferences, available_for_play: !play },
                  presence: { ...(p.presence ?? {}), show_in_community_stream: !play },
                }))
              }
              className={[
                "rounded-xl border p-4 text-left transition",
                "flex flex-col justify-center",
                play
                  ? "bg-blue-600/30 border-blue-400 ring-1 ring-blue-300 shadow-inner"
                  : "bg-blue-900/10 border-blue-800 hover:bg-blue-900/20",
              ].join(" ")}
              title="Toggle Available for Play"
            >
              <div className="text-xl font-bold text-purple-300">Available for Play</div>
              <div className="text-sm opacity-90 mt-1">
                Enables Community Stream; when Play-only, everything except Meetup Options & Preferred LGS is editable.
              </div>
            </button>
          </div>

          <div className="text-center text-sm">
            Mode: <span className="font-medium">{mode}</span>
          </div>
          <div className="text-center text-xs text-white/90">
            Trade-only hides your profile from the Community Stream. Play-only hides your profile & collection from the Marketplace.
          </div>
        </CardContent>
      </Card>

      {/* === Location & Search === */}
      <Card className={`text-white border-slate-700 shadow-lg bg-gradient-to-br from-[#0b1120] via-[#0c1222] to-[#0d1324] ${!play ? "opacity-60" : ""}`}>
        <CardHeader className="px-6 py-4 bg-[#2a1650] text-amber-300 border-b border-amber-500/30 rounded-t-xl">
          <CardTitle className="text-amber-300">Location & Search</CardTitle>
          <CardDescription className="text-amber-200">
            Use your current location; we’ll find nearby WPN stores.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 px-6 py-6">
          <div className="flex items-center justify-between">
            <span className="font-medium">Always get location when logged in (without requesting)</span>
            <button
              type="button"
              aria-pressed={autoLocationOnLogin}
              onClick={() => void handleToggleAutoLocation(!autoLocationOnLogin)}
              className={[
                "rounded-lg border px-3 py-2 text-sm font-medium transition",
                autoLocationOnLogin
                  ? "bg-amber-400 text-black border-amber-300"
                  : "bg-gray-800 text-gray-100 border-white/10 hover:bg-gray-700",
              ].join(" ")}
              title="Toggle auto-get current location on login"
            >
              {autoLocationOnLogin ? "Enabled" : "Disabled"}
            </button>
          </div>
          <p className="text-xs text-white/80">
            When enabled, the app updates to your current location on login; when disabled, you’ll be prompted to choose.
          </p>

          <div className="flex items-center justify-between pt-2">
            <span className="font-medium">Location</span>
            <Switch
              checked={profile.visibility.location}
              onChange={(v) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, location: v } }))}
              disabled={!play}
              label="Show location on profile"
            />
          </div>

            <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={useMyLocation} aria-label="Use my location">
              Use my location
            </Button>
            <div className="text-sm" aria-live="polite">
              {profile.location?.lat != null && profile.location?.lng != null ? `coords set ✓` : "No location set yet"}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* === Play Preferences === */}
      <Card className={`text-white border-slate-700 shadow-lg bg-gradient-to-br from-[#0b1120] via-[#0c1222] to-[#0d1324] ${!play ? "opacity-60" : ""}`}>
        <CardHeader className="px-6 py-4 bg-[#2a1650] text-amber-300 border-b border-amber-500/30 rounded-t-xl">
          <CardTitle className="text-amber-300">Play Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 px-6 py-6">
          <div className="flex items-center justify-between">
            <span className="font-medium">Preferred Formats</span>
            <Switch
              checked={profile.visibility.play_formats}
              onChange={(v) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, play_formats: v } }))}
              disabled={!play}
              label="Show formats on profile"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {PLAY_FORMATS.map((f) => {
              const selected = profile.play_formats.includes(f);
              return (
                <button
                  key={f}
                  type="button"
                  disabled={!play}
                  className={`px-3 py-2 rounded text-sm ${selected ? "btn-pixel btn-pixel-primary" : "btn-pixel btn-pixel-secondary"} ${!play ? "pointer-events-none" : ""}`}
                  onClick={() =>
                    setProfile((p) => ({
                      ...p,
                      play_formats: selected ? p.play_formats.filter((x) => x !== f) : [...p.play_formats, f],
                    }))
                  }
                  aria-pressed={selected}
                >
                  {f}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between">
            <span className="font-medium">Commander Brackets</span>
            <Switch
              checked={profile.visibility.commander_tiers}
              onChange={(v) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, commander_tiers: v } }))}
              disabled={!play}
              label="Show commander brackets on profile"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {BRACKETS.map((b) => {
              const selected = profile.commander_brackets.includes(b.id);
              return (
                <div key={b.id} className={`p-4 rounded border ${selected ? "border-amber-400 bg-black/30" : "border-gray-700 bg-black/10"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div><h4 className="font-semibold">{b.title}</h4></div>
                    <Switch
                      checked={selected}
                      onChange={(checked) =>
                        setProfile((p) => ({
                          ...p,
                          commander_brackets: checked
                            ? ([...p.commander_brackets, b.id] as BracketId[])
                            : (p.commander_brackets.filter((x) => x !== b.id) as BracketId[]),
                        }))
                      }
                      disabled={!play}
                      label={`Toggle ${b.title}`}
                    />
                  </div>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-sm">Details</summary>
                    <div className="mt-2 text-sm space-y-2">
                      <p><span className="font-medium">Experience:</span> {b.experience}</p>
                      <p><span className="font-medium">Deck Building:</span> {b.deckBuilding}</p>
                    </div>
                  </details>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between">
            <span className="font-medium">Competitive Preferences</span>
            <Switch
              checked={profile.visibility.competitive_preference}
              onChange={(v) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, competitive_preference: v } }))}
              disabled={!play}
              label="Show competitive preferences on profile"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {COMPETITIVE_CHOICES.map((opt) => {
              const selected = profile.competitive_preferences.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={!play}
                  className={`px-3 py-2 rounded text-sm ${selected ? "btn-pixel btn-pixel-primary" : "btn-pixel btn-pixel-secondary"} ${!play ? "pointer-events-none" : ""}`}
                  onClick={() =>
                    setProfile((p) => ({
                      ...p,
                      competitive_preferences: selected ? p.competitive_preferences.filter((x) => x !== opt) : [...p.competitive_preferences, opt],
                    }))
                  }
                  aria-pressed={selected}
                >
                  {opt}
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex items-center justify-between">
              <label htmlFor="play-radius">Play radius (mi)</label>
              <Switch
                checked={profile.visibility.play_radius}
                onChange={(v) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, play_radius: v } }))}
                disabled={!play}
                label="Show play radius"
              />
            </div>
            <input
              id="play-radius"
              type="number"
              className="border rounded px-3 py-2 text-black"
              disabled={!play}
              value={profile.play_radius}
              onChange={(e) => setProfile((p) => ({ ...p, play_radius: parseInt(e.target.value || "0", 10) }))}
              placeholder="10"
            />

            <div className="flex items-center justify-between">
              <label htmlFor="trade-radius">Trade radius (mi)</label>
              <Switch
                checked={profile.visibility.trade_radius}
                onChange={(v) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, trade_radius: v } }))}
                disabled={!play}
                label="Show trade radius"
              />
                       </div>
            <input
              id="trade-radius"
              type="number"
              className="border rounded px-3 py-2 text-black"
              disabled={!play}
              value={profile.trade_radius}
              onChange={(e) => setProfile((p) => ({ ...p, trade_radius: parseInt(e.target.value || "0", 10) }))}
              placeholder="25"
            />
          </div>
        </CardContent>
      </Card>

      {/* === Meetup Options & Preferred LGS === */}
      <Card className="text-white border-slate-700 shadow-lg bg-gradient-to-br from-[#0b1120] via-[#0c1222] to-[#0d1324]">
        <CardHeader className="px-6 py-4 bg-[#2a1650] text-amber-300 border-b border-amber-500/30 rounded-t-xl">
          <CardTitle className="text-amber-300">Meetup Options & Preferred LGS</CardTitle>
          <CardDescription className="text-amber-200">
             Uses Wizards Store Locator when available; falls back to local map data.
           </CardDescription>
         </CardHeader>
        <CardContent className="space-y-4 px-6 py-6">
          <div className="flex items-center justify-between">
            <span className="font-medium">Preferred Meetup Types</span>
            <Switch
              checked={profile.visibility.meetup_types}
              onChange={(v) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, meetup_types: v } }))}
              disabled={!trade}
              label="Show meetup types"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {MEETUP_TYPES.map((t) => {
              const selected = profile.meetup_types.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  disabled={!trade}
                  className={`px-3 py-2 rounded text-sm ${selected ? "btn-pixel btn-pixel-primary" : "btn-pixel btn-pixel-secondary"} ${!trade ? "pointer-events-none" : ""}`}
                  onClick={() =>
                    setProfile((p) => ({
                      ...p,
                      meetup_types: selected ? p.meetup_types.filter((x) => x !== t) : [...p.meetup_types, t],
                    }))
                  }
                  aria-pressed={selected}
                >
                  {t}
                               </button>
              );
            })}
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <label htmlFor="preferred-lgs" className="font-medium">Preferred LGS</label>
              <Switch
                checked={profile.visibility.preferred_lgs ?? true}
                onChange={(v) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, preferred_lgs: v } }))}
                disabled={!trade}
                label="Show preferred LGS"
              />
            </div>

            <div className="flex gap-2">
              <input
                id="preferred-lgs"
                className="border rounded px-3 py-2 text-black flex-1"
                placeholder="Pick from results or type a store"
                // Always editable
                value={profile.preferred_lgs ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setProfile((p) => ({ ...p, preferred_lgs: v }));
                  setLgsQuery(v);
                }}
                onFocus={() => {}}
              />
              <Button
                variant="outline"
                disabled={!profile.location?.lat || !profile.location?.lng || lgsLoading}
                onClick={() => void handleLgsSearch()}
                title={
                  !profile.location?.lat
                    ? "Click 'Use my location' first"
                    : lgsCurrentRadius == null
                      ? `Search nearby (start ${lgsBaseRadius} mi)`
                      : `Expand radius to ${(lgsCurrentRadius ?? 0) + 5} mi`
                }
                aria-label="Search / expand store radius"
              >
                {lgsLoading
                  ? "Searching…"
                  : lgsCurrentRadius == null
                    ? `Search (${lgsBaseRadius} mi)`
                    : `Show more (+5 mi → ${(lgsCurrentRadius ?? 0) + 5} mi)`}
              </Button>
            </div>

            {(lgsLoading || lgsAll.length > 0) && (
              <div className="grid gap-2 mt-2">
                {lgsLoading && (
                  <div className="px-3 py-2 text-sm text-white/80">
                    Searching{lgsAll.length ? " (updating…)" : " near you…"}
                  </div>
                )}

                {!lgsLoading && lgsAll.length === 0 && (
                  <div className="px-3 py-2 text-sm text-white/80">
                    No stores yet. Try expanding the radius.
                  </div>
                )}

                {lgsAll.length > 0 && (
                  <>
                    <div className="flex flex-wrap items-center gap-3 px-1 text-sm">
                      <span>
                        Showing {displayLgs.length} of {filteredAllLgs.length} result
                        {filteredAllLgs.length === 1 ? "" : "s"}
                        {lgsCurrentRadius != null && <> (radius {lgsCurrentRadius} mi)</>}
                      </span>
                      {lgsQuery && (
                        <button
                          className="text-xs underline hover:opacity-80"
                          onClick={() => setLgsQuery("")}
                        >
                          Clear filter
                        </button>
                      )}
                    </div>

                    <div className={filteredAllLgs.length > 15 ? "max-h-80 overflow-auto rounded border border-gray-800" : "rounded border border-gray-800"}>
                      {displayLgs.map((s, i) => (
                        <button
                          key={`${s.id ?? s.name}-${i}`}
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-black/25 border-b border-gray-800 last:border-b-0"
                          onClick={() => {
                            setProfile((p) => ({ ...p, preferred_lgs: s.name }));
                            void saveLgsSelection(s);
                          }}
                          aria-label={`Select ${s.name}`}
                          title={s.address ?? s.name}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{s.name}</div>
                            {s.address && (
                              <div className="text-xs opacity-80 truncate">{s.address}</div>
                            )}
                          </div>
                          <div className="text-xs opacity-70 pl-2 shrink-0">
                            {s.distanceKm != null ? `${s.distanceKm.toFixed(1)} km` : ""}
                          </div>
                        </button>
                      ))}
                    </div>

                    {filteredAllLgs.length > displayLgs.length && (
                      <div className="mt-2 flex items-center gap-2">
                        {!showPagination ? (
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowPagination(true);
                              setLgsPage(1);
                            }}
                          >
                            Load more
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              onClick={() => setLgsPage((p) => Math.max(0, p - 1))}
                              disabled={lgsPage <= 0}
                            >
                              Prev
                            </Button>
                            <span className="text-xs opacity-80">
                              Page {lgsPage + 1} of {Math.max(1, totalPages)}
                            </span>
                            <Button
                              variant="outline"
                              onClick={() => setLgsPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                              disabled={lgsPage + 1 >= totalPages}
                            >
                              Next
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* === Community & Marketplace === */}
      <Card className="text-white border-slate-700 shadow-lg bg-gradient-to-br from-[#0b1120] via-[#0c1222] to-[#0d1324]">
        <CardHeader className="px-6 py-4 bg-[#2a1650] text-amber-300 border-b border-amber-500/30 rounded-t-xl">
          <CardTitle className="text-amber-300">Community & Marketplace</CardTitle>
          <CardDescription className="text-amber-200">
             Presence is derived from the availability buttons above.
           </CardDescription>
         </CardHeader>
        <CardContent className="space-y-3 px-6 py-6">
          <div className="text-sm">
            <span className="opacity-90">Marketplace visibility: </span>
            <span className="font-medium">{trade ? "Shown" : "Hidden (Trade OFF)"}</span>
          </div>
          <div className="text-sm">
            <span className="opacity-90">Community Stream visibility: </span>
            <span className="font-medium">{play ? "Shown" : "Hidden (Play OFF)"}</span>
          </div>

          <div className={`${!play ? "opacity-60" : ""}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Allow Collection Browsing</p>
                <p className="text-xs text-white/90">Let others see your public cards</p>
              </div>
              <Switch
                checked={Boolean(profile.preferences.collection_browsing)}
                onChange={(v) => setProfile((p) => ({ ...p, preferences: { ...p.preferences, collection_browsing: v } }))}
                disabled={!play}
                label="Allow collection browsing"
              />
            </div>

            <div className="flex items-center justify-between mt-2">
              <div>
                <p className="font-medium">Public Wishlist</p>
                <p className="text-xs text-white/90">Show desired cards on profile</p>
              </div>
              <Switch
                checked={Boolean(profile.preferences.wishlist_is_public)}
                onChange={(v) => setProfile((p) => ({ ...p, preferences: { ...p.preferences, wishlist_is_public: v } }))}
                disabled={!play}
                label="Public wishlist"
              />
            </div>
          </div>

          <div className="grid gap-2 mt-4">
            <label htmlFor="followers-select" className="font-medium">Followers</label>
            <select
              id="followers-select"
              className="border rounded px-3 py-2 text-black"
              value={profile.follow_privacy}
              onChange={(e) => setProfile((p) => ({ ...p, follow_privacy: e.target.value as FollowPrivacy }))}
            >
              <option value="open">Public — Anyone can follow</option>
              <option value="request">Review — Approve follow requests</option>
              <option value="private">No followers — Private</option>
            </select>
            <p className="text-xs text-white/90">
              We’ll add a screen to review/approve follow requests for “Review” mode.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* === Email Notifications === */}
      <Card className="text-white border-slate-700 shadow-lg bg-gradient-to-br from-[#0b1120] via-[#0c1222] to-[#0d1324]">
        <CardHeader className="px-6 py-4 bg-[#2a1650] text-amber-300 border-b border-amber-500/30 rounded-t-xl">
          <CardTitle className="text-amber-300">Email Notifications</CardTitle>
          <CardDescription className="text-amber-200">
             Choose what we email you about. You can change these any time.
           </CardDescription>
         </CardHeader>
        <CardContent className="space-y-4 px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Offers (trade or purchase)</p>
              <p className="text-xs text-white/90">When someone sends you an offer on one of your cards.</p>
            </div>
              <Switch
                checked={!!profile.email_prefs?.offers}
                onChange={(v) => setProfile((p) => ({ ...p, email_prefs: { ...p.email_prefs, offers: v } }))}
                label="Offers email"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">New followers</p>
                <p className="text-xs text-white/90">When someone follows you (if your follow privacy allows).</p>
              </div>
              <Switch
                checked={!!profile.email_prefs?.follows}
                onChange={(v) => setProfile((p) => ({ ...p, email_prefs: { ...p.email_prefs, follows: v } }))}
                label="Followers email"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Direct messages</p>
                <p className="text-xs text-white/90">When you receive a new direct message.</p>
              </div>
              <Switch
                checked={!!profile.email_prefs?.dms}
                onChange={(v) => setProfile((p) => ({ ...p, email_prefs: { ...p.email_prefs, dms: v } }))}
                label="DMs email"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">News & updates</p>
                <p className="text-xs text-white/90">Product updates and community news.</p>
              </div>
              <Switch
                checked={!!profile.email_prefs?.marketing}
                onChange={(v) => setProfile((p) => ({ ...p, email_prefs: { ...p.email_prefs, marketing: v } }))}
                label="Marketing email"
              />
            </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between px-1">
        {msg && <span className="text-sm">{msg}</span>}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.location.reload()} aria-label="Reset form">
            Reset
          </Button>
          <Button onClick={onSave} disabled={saving} aria-label="Save changes">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
      </main>

      {/* Only render the LGS modal when open to avoid any overlay capturing clicks while "closed" */}
     {lgsUpgradeOpen ? <LgsUpgradeModal open={lgsUpgradeOpen} onOpenChange={onLgsOpenChange} /> : null}
    </>
  );
}
